import { Temporal } from "@js-temporal/polyfill";

export type AppointmentEnd = {
  endDate: string;
  endTime: string;
};

type AppointmentStart = {
  startDate: string;
  startTime: string;
  timeZone?: string | null;
};

type AppointmentRange = AppointmentStart & AppointmentEnd;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME = /^\d{2}:\d{2}$/;

function parsePlainDateTime(
  date: string,
  time: string,
): Temporal.PlainDateTime | null {
  if (!ISO_DATE.test(date) || !ISO_TIME.test(time)) return null;

  try {
    return Temporal.PlainDateTime.from(`${date}T${time}`);
  } catch {
    return null;
  }
}

function zonedDateTime(
  value: Temporal.PlainDateTime,
  timeZone: string | null | undefined,
): Temporal.ZonedDateTime | null {
  if (!timeZone?.trim()) return null;

  try {
    return value.toZonedDateTime(timeZone);
  } catch {
    // Legacy appointments can contain missing or invalid timezone names. The
    // wall-clock fallback below still preserves their explicit date/time
    // range instead of preventing the patient from correcting the record.
    return null;
  }
}

function serializePlainDateTime(value: Temporal.PlainDateTime): AppointmentEnd {
  return {
    endDate: value.toPlainDate().toString(),
    endTime: value.toPlainTime().toString({ smallestUnit: "minute" }),
  };
}

/**
 * Add an explicit elapsed duration to an appointment's wall-clock start.
 *
 * When a valid IANA timezone is available, Temporal performs the addition on
 * the zoned instant. This makes a 60-minute appointment remain 60 elapsed
 * minutes across a daylight-saving transition. Legacy/invalid zones fall back
 * to plain wall-clock arithmetic so the form remains editable.
 */
export function calculateAppointmentEnd({
  startDate,
  startTime,
  durationMinutes,
  timeZone,
}: AppointmentStart & { durationMinutes: number }): AppointmentEnd | null {
  if (!Number.isInteger(durationMinutes) || durationMinutes <= 0) return null;

  const start = parsePlainDateTime(startDate, startTime);
  if (!start) return null;

  const zonedStart = zonedDateTime(start, timeZone);
  const end = zonedStart
    ? zonedStart.add({ minutes: durationMinutes }).toPlainDateTime()
    : start.add({ minutes: durationMinutes });

  return serializePlainDateTime(end);
}

/** Restore a positive whole-minute duration from persisted start/end fields. */
export function calculateAppointmentDurationMinutes({
  startDate,
  startTime,
  endDate,
  endTime,
  timeZone,
}: AppointmentRange): number | null {
  const start = parsePlainDateTime(startDate, startTime);
  const end = parsePlainDateTime(endDate, endTime);
  if (!start || !end) return null;

  const zonedStart = zonedDateTime(start, timeZone);
  const zonedEnd = zonedDateTime(end, timeZone);
  const startMilliseconds = (zonedStart ?? start.toZonedDateTime("UTC"))
    .epochMilliseconds;
  const endMilliseconds = (zonedEnd ?? end.toZonedDateTime("UTC"))
    .epochMilliseconds;
  const durationMinutes = (endMilliseconds - startMilliseconds) / (60 * 1000);

  return Number.isInteger(durationMinutes) && durationMinutes > 0
    ? durationMinutes
    : null;
}
